/**
 * Validadores de datos
 * Monedero Digital NFC
 */

import { LIMITS } from './constants';

/**
 * Valida que un email tenga formato correcto
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Valida que una contraseña cumpla con los requisitos mínimos
 */
export const validatePassword = (password) => {
  if (!password || password.length < 4) {
    return { valid: false, message: 'La contraseña debe tener al menos 4 caracteres' };
  }
  return { valid: true };
};

/**
 * Valida un monto de recarga
 */
export const validateRechargeAmount = (amount, currentBalance = 0) => {
  const numAmount = Number(amount);

  if (isNaN(numAmount) || numAmount <= 0) {
    return { valid: false, message: 'El monto debe ser un número válido mayor a cero' };
  }

  if (numAmount < LIMITS.MIN_RECARGA) {
    return { valid: false, message: `El monto mínimo de recarga es ${formatCurrency(LIMITS.MIN_RECARGA)}` };
  }

  if (numAmount > LIMITS.MAX_RECARGA) {
    return { valid: false, message: `El monto máximo de recarga es ${formatCurrency(LIMITS.MAX_RECARGA)}` };
  }

  if (currentBalance + numAmount > LIMITS.MAX_SALDO) {
    return { 
      valid: false, 
      message: `El saldo máximo permitido es ${formatCurrency(LIMITS.MAX_SALDO)}. Saldo actual: ${formatCurrency(currentBalance)}` 
    };
  }

  return { valid: true };
};

/**
 * Valida un monto de pago
 */
export const validatePaymentAmount = (amount, availableBalance) => {
  const numAmount = Number(amount);

  if (isNaN(numAmount) || numAmount <= 0) {
    return { valid: false, message: 'El monto debe ser un número válido mayor a cero' };
  }

  if (numAmount < LIMITS.MIN_PAGO) {
    return { valid: false, message: `El monto mínimo de pago es ${formatCurrency(LIMITS.MIN_PAGO)}` };
  }

  if (numAmount > availableBalance) {
    return { 
      valid: false, 
      message: `Saldo insuficiente. Disponible: ${formatCurrency(availableBalance)}` 
    };
  }

  return { valid: true };
};

/**
 * Valida que un carrito de productos sea válido
 */
export const validateCart = (cart) => {
  if (!cart || cart.length === 0) {
    return { valid: false, message: 'Debes agregar al menos un producto' };
  }

  const hasInvalidQuantity = cart.some(item => !item.cantidad || item.cantidad <= 0);
  if (hasInvalidQuantity) {
    return { valid: false, message: 'Todos los productos deben tener cantidad válida' };
  }

  return { valid: true };
};

/**
 * Valida datos de un producto
 */
export const validateProduct = (product) => {
  const errors = {};

  if (!product.nombre || product.nombre.trim().length < 2) {
    errors.nombre = 'El nombre debe tener al menos 2 caracteres';
  }

  if (!product.precio || Number(product.precio) <= 0) {
    errors.precio = 'El precio debe ser mayor a cero';
  }

  if (!product.categoria) {
    errors.categoria = 'Debes seleccionar una categoría';
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Valida que un usuario tenga los campos requeridos
 */
export const validateUser = (user) => {
  const errors = {};

  if (!user.username || user.username.trim().length < 3) {
    errors.username = 'El usuario debe tener al menos 3 caracteres';
  }

  if (!user.password || user.password.length < 4) {
    errors.password = 'La contraseña debe tener al menos 4 caracteres';
  }

  if (!user.nombre || user.nombre.trim().length < 3) {
    errors.nombre = 'El nombre debe tener al menos 3 caracteres';
  }

  if (user.email && !validateEmail(user.email)) {
    errors.email = 'El email no es válido';
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Valida un serial number de NFC
 */
export const validateNFCSerial = (serialNumber) => {
  if (!serialNumber || typeof serialNumber !== 'string') {
    return { valid: false, message: 'Serial number inválido' };
  }

  // Formato típico: "04:A1:B2:C3:D4:E5:F6"
  const nfcPattern = /^[0-9A-F]{2}(:[0-9A-F]{2})+$/i;
  
  if (!nfcPattern.test(serialNumber)) {
    return { valid: false, message: 'Formato de serial number inválido' };
  }

  return { valid: true };
};

/**
 * Valida que un objeto tenga las propiedades requeridas
 */
export const validateRequiredFields = (obj, requiredFields) => {
  const missing = requiredFields.filter(field => !obj[field]);
  
  if (missing.length > 0) {
    return {
      valid: false,
      message: `Campos requeridos faltantes: ${missing.join(', ')}`
    };
  }

  return { valid: true };
};

// Helper para formatear moneda (usado en validadores)
function formatCurrency(amount) {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
  }).format(amount);
}

export default {
  validateEmail,
  validatePassword,
  validateRechargeAmount,
  validatePaymentAmount,
  validateCart,
  validateProduct,
  validateUser,
  validateNFCSerial,
  validateRequiredFields,
};
